import {useEffect, useRef, useState} from 'react';
import {useParams as useRouteParams} from 'react-router-dom';
import {fetchCompositionReviewStatusService} from 'store/services/compositionServices';
import {useAblyWebsocket} from 'utils/functions/createConnection';
import {useLongPoll} from 'utils/hooks/useLongPoll';
import {CompositionReviewedStatus} from 'utils/types/newReviewed';
import NewReviewed from 'containers/NewReviewed';
import FeedbackLoadingView from './components/FeedbackLoadingView';
import SimplifiedFeedbackHeader from './components/SimplifiedFeedbackHeader';
import SimplifiedFeedbackText from './components/SimplifiedFeedbackText';
import FeedbackCommentCards from './components/FeedbackCommentCards';
import FeedbackLevelCard from './components/FeedbackLevelCard';
import SimplifiedFeedbackClosing from './components/SimplifiedFeedbackClosing';
import {fetchSimplifiedFeedbackGradesService} from './simplifiedFeedback.service';
import {SimplifiedFeedbackData} from './types';
import {getSimplifiedFeedbackLevelSupportPhrase} from './simplifiedFeedbackLevels';
import styles from './SimplifiedFeedback.module.scss';

function SimplifiedFeedback(): JSX.Element {
  const [isLoadingReview, setIsLoadingReview] = useState(true);
  const [compositionStatus, setCompositionStatus] =
    useState<CompositionReviewedStatus>();
  const [simplifiedFeedbackData, setSimplifiedFeedbackData] =
    useState<SimplifiedFeedbackData>();

  const {composition_id} = useRouteParams();
  const isMountedRef = useRef(true);
  // The review may not have finished on the 1st attempt (endpoint returns `{}`, see
  // simplifiedFeedback.service.ts) — this ref prevents fetching the grades again after they've
  // already arrived, without depending on the `simplifiedFeedbackData` value in a closure that
  // may be stale (getCompositionStatus runs from inside the long poll/Ably, whose `requestFn` was
  // captured in a previous render).
  const gradesLoadedRef = useRef(false);
  // Identifies the current effect run: a request started for a previous composition_id can still
  // resolve after the effect re-ran (the mount ref is already back to true), so each async path
  // captures the run id at start and drops its result if the run is no longer current.
  const runIdRef = useRef(0);

  const compositionStatusLongPoll = useLongPoll(
    getCompositionStatus,
    15 * 1000,
    10,
  );

  const ablyWebsocket = useAblyWebsocket({
    onError: () => compositionStatusLongPoll.start(),
  });

  function fetchGrades() {
    if (gradesLoadedRef.current) return;

    const runId = runIdRef.current;

    fetchSimplifiedFeedbackGradesService(composition_id)
      .then(({data}) => {
        if (!isMountedRef.current || runId !== runIdRef.current || !data)
          return;
        gradesLoadedRef.current = true;
        setSimplifiedFeedbackData(data);
      })
      .catch(() => {
        // TODO: deal with connection failure, same open question as NewReviewed
        // (src/containers/NewReviewed/index.tsx:421-423).
      });
  }

  async function getCompositionStatus() {
    const runId = runIdRef.current;

    try {
      const {data} = await fetchCompositionReviewStatusService(composition_id);
      const {status, automatic_review} = data;

      if (!isMountedRef.current || runId !== runIdRef.current) return;

      setIsLoadingReview(false);
      setCompositionStatus(data);

      if (automatic_review && status !== 'reviewed') {
        ablyWebsocket.initialize(composition_id);
      } else {
        compositionStatusLongPoll.stop();
      }

      // The composition may have become "reviewed" after the 1st attempt to fetch the grades
      // (which may have come back empty while the review was still running) — try again here,
      // without which the screen would stay stuck on loading forever (Copilot finding, confirmed
      // by reading the contract documented in simplifiedFeedback.service.ts).
      if (status === 'reviewed') {
        fetchGrades();
      }
    } catch (error) {
      // TODO: deal with connection failure, same open question as NewReviewed
      // (src/containers/NewReviewed/index.tsx:421-423).
    }
  }

  useEffect(() => {
    isMountedRef.current = true;
    runIdRef.current += 1;
    gradesLoadedRef.current = false;
    setIsLoadingReview(true);
    setCompositionStatus(undefined);
    setSimplifiedFeedbackData(undefined);

    getCompositionStatus();
    fetchGrades();

    return () => {
      isMountedRef.current = false;
      // Tear down the realtime connection and its long-poll fallback on unmount, otherwise a
      // student leaving before the review finishes leaks the Ably connection and its interval.
      ablyWebsocket.unsubscribe();
      compositionStatusLongPoll.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only composition_id should restart
    // the flow; getCompositionStatus/fetchGrades are recreated every render but use the same
    // values via closure, including them would create an effect loop.
  }, [composition_id]);

  const isZeroGrade = compositionStatus?.review?.zero_grader?.is_zeroed;

  // Zeroing continues to be handled by NewReviewed's existing flow — we don't duplicate that
  // logic here (see tarefas/devolutiva-simplificada-quinto-ano/design.md).
  if (isZeroGrade) {
    return <NewReviewed />;
  }

  if (
    isLoadingReview ||
    compositionStatus?.status !== 'reviewed' ||
    !simplifiedFeedbackData
  ) {
    return <FeedbackLoadingView />;
  }

  const paragraphs = compositionStatus?.paragraphs ?? [];

  return (
    <main className={styles.container} data-focusid="container">
      <SimplifiedFeedbackHeader />

      <div className={styles.content}>
        <FeedbackCommentCards
          criteriaGrades={simplifiedFeedbackData.criteriaGrades}
        />

        <FeedbackLevelCard
          levelLabel={simplifiedFeedbackData.level.label}
          stars={simplifiedFeedbackData.level.stars}
          supportPhrase={getSimplifiedFeedbackLevelSupportPhrase(
            simplifiedFeedbackData.level.label,
          )}
        />

        <SimplifiedFeedbackText paragraphs={paragraphs} />

        <SimplifiedFeedbackClosing />
      </div>
    </main>
  );
}

export default SimplifiedFeedback;
